import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { useSelector } from 'react-redux';

import {
    Box,
    Typography,
    Button,
    Container,
    useTheme,
    useMediaQuery,
} from '@thirdspacelearning/library';

import { useKeycloakAuth } from '@tsl-frontend/tsl-keycloak-auth';

import { isPrimaryStudent } from '@utils';

import { RoutePaths } from '@routes';
import {
    ArnieTapleyDecoration,
    TutorPupilWavingDecoration,
} from '@assets/welcome';
import { useAppDispatch } from '@store';
import {
    markWelcomeAsCompleted,
    userWelcomeRequiredSelector,
} from '@store/programme';
import { regionAppConfig } from '@locales';

interface Props {}

const Welcome: FC<Props> = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { user } = useKeycloakAuth();

    const isWelcomeRequired = useSelector(userWelcomeRequiredSelector);

    const videoUrl = isPrimaryStudent(user)
        ? regionAppConfig.primaryWelcomeLink
        : regionAppConfig.secondaryWelcomeLink;

    const theme = useTheme();
    const mediaQuery = useMediaQuery(theme.breakpoints.up('lg'));

    useEffect(() => {
        if (!isWelcomeRequired) {
            navigate(RoutePaths.DASHBOARD);
        }
    }, [isWelcomeRequired, navigate]);

    const completeWelcomeStage = () => {
        dispatch(markWelcomeAsCompleted());
    };

    const enterFullscreen = () => {
        const fullscreenButton = document.querySelectorAll(
            '[title="Fullscreen"]',
        );

        if (fullscreenButton.length) {
            (fullscreenButton[0] as HTMLButtonElement).click();
        }
    };

    const exitFullscreen = () => {
        document.exitFullscreen().catch(() => {
            // on error do nothing as we're already out of fullscreen
        });
    };

    return (
        <Box
            position="relative"
            width="100%"
            height="100%"
            data-testid="welcome-page"
        >
            <Container maxWidth="md">
                <Box mb={2} marginLeft="15%">
                    <Typography variant="h3">
                        <Box
                            fontWeight="fontWeightBold"
                            component="b"
                            data-testid="user-greeting"
                        >
                            {`👋 Welcome ${user?.first_name}!`}
                        </Box>
                    </Typography>
                </Box>

                <Box mb={1} marginLeft="15%">
                    <Typography variant="h5">
                        <Box fontWeight="fontWeightBold" component="b">
                            Press the play button to learn what you will do with
                            us
                        </Box>
                    </Typography>
                </Box>

                <Box height="50%" display="flex" justifyContent="center">
                    <ReactPlayer
                        url={videoUrl}
                        controls={true}
                        onStart={enterFullscreen}
                        onEnded={exitFullscreen}
                        config={{
                            wistia: {
                                options: {
                                    videoFoam: {
                                        minWidth: '70%',
                                    },
                                },
                            },
                        }}
                        width="70%"
                        height="auto"
                    />
                </Box>

                <Box
                    display="flex"
                    justifyContent="flex-end"
                    mt={1}
                    position="relative"
                    width="85%"
                >
                    <Button
                        color="primary"
                        variant="contained"
                        size="medium"
                        children="Let’s get started"
                        onClick={completeWelcomeStage}
                        data-testid="lets-get-started-btn"
                    />
                </Box>
            </Container>

            {mediaQuery && (
                <>
                    <Box position="absolute" left="-20px" bottom="-30px">
                        <ArnieTapleyDecoration />
                    </Box>
                    <Box
                        position="absolute"
                        right="-24px"
                        bottom="30%"
                        top="10px"
                    >
                        <TutorPupilWavingDecoration />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default Welcome;
